// StaffCMS: Limited CRUD (Highlights, Schedule, Hotels)

import React, { useState, useEffect } from 'react';
import StaffLayout from '../../layouts/StaffLayout';
import { Box, Typography, Tabs, Tab, Paper, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Card, CardContent, Chip, Divider, TextField, Button } from '@mui/material';
import SponsorsTab from '../cms/SponsorsTab';
import Alert from '@mui/material/Alert';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import api from '../../services/api';

// Helper: Retry API call on 429 with exponential backoff
async function apiWithRetry(fn, retries = 3, delay = 1000) {
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (e) {
      if (e?.response?.status === 429 && i < retries) {
        await new Promise(res => setTimeout(res, delay * Math.pow(2, i)));
        continue;
      }
      throw e;
    }
  }
}

import ContactTab from '../cms/ContactTab';
const cmsTabs = [
  { label: 'Slider & Countdown', key: 'slider' },
  { label: 'Schedule', key: 'schedule' },
  { label: 'Highlights', key: 'highlight' },
  { label: 'Hotels', key: 'hotels' },
  { label: 'Sponsors', key: 'sponsors' },
  { label: 'Contact', key: 'contact' },
  { label: 'About', key: 'about' },
  { label: 'Landing Backgrounds', key: 'landing' },
];

function StaffCMS() {
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cms, setCms] = useState({});
  const [sliders, setSliders] = useState([]);
  const [sliderConfig, setSliderConfig] = useState({ width: 1200, height: 420, countdownDate: '', beltMessages: [] });
  const [hotels, setHotels] = useState([]);
  // About tab state
  const [aboutForm, setAboutForm] = useState({ heading: '', text: '', image: '', link: '' });
  const [aboutLoading, setAboutLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        // Fetch CMS data sequentially to avoid parallel rate limit hits
        const cmsRes = await apiWithRetry(() => api.get('/api/cms'));
        const slidersRes = await apiWithRetry(() => api.get('/api/cms/sliders'));
        const sliderConfigRes = await apiWithRetry(() => api.get('/api/cms/slider-config'));
        const hotelsRes = await apiWithRetry(() => api.get('/api/hotels'));
        const cmsArr = Array.isArray(cmsRes.data) ? cmsRes.data : (cmsRes.data?.data || []);
        const cmsObj = {};
        cmsArr.forEach(i => { if (i.type) cmsObj[i.type] = i; });
        setCms(cmsObj);
        setSliders(slidersRes.data || []);
        setSliderConfig({
          width: sliderConfigRes.data?.width || 1200,
          height: sliderConfigRes.data?.height || 420,
          countdownDate: sliderConfigRes.data?.countdownDate || '',
          beltMessages: sliderConfigRes.data?.beltMessages || [],
        });
        setHotels(hotelsRes.data?.hotels || hotelsRes.data || []);
        setError('');
      } catch (e) {
        if (e?.response?.status === 429) {
          setError('Too many requests. Please wait and try again.');
        } else {
          setError('Failed to load CMS data.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load About content from CMS when About tab is active
  useEffect(() => {
    if (tab === 4) {
      setAboutLoading(true);
      api.get('/api/cms?types=about')
        .then(res => {
          let aboutItem = null;
          if (Array.isArray(res.data)) {
            const aboutItems = res.data.filter(i => i.type === 'about');
            if (aboutItems.length > 0) {
              aboutItems.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
              aboutItem = aboutItems[0];
            }
          }
          setAboutForm(aboutItem?.contentJSON || { heading: '', text: '', image: '', link: '' });
        })
        .catch(() => setAboutForm({ heading: '', text: '', image: '', link: '' }))
        .finally(() => setAboutLoading(false));
    }
  }, [tab]);

  // Landing backgrounds state for staff
  const [landingForm, setLandingForm] = useState({ backgrounds: {} });
  const [landingLoading, setLandingLoading] = useState(false);

  useEffect(() => {
    // load landing when tab active
    if (tab === 7) {
      api.get('/api/cms').then(res => {
        const items = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        const landing = (items || []).find(i => i.type === 'landing');
        setLandingForm({ backgrounds: landing?.contentJSON?.backgrounds || {} });
      }).catch(() => {});
    }
  }, [tab]);

  const saveLandingForStaff = async () => {
    setLandingLoading(true);
    try {
      const current = await api.get('/api/cms');
      const landingItem = (current.data || current).find(i => i.type === 'landing');
      const payload = { contentJSON: { backgrounds: landingForm.backgrounds || {} } };
      if (landingItem && landingItem._id) await api.put(`/api/cms/${landingItem._id}`, payload);
      else await api.post('/api/cms', { type: 'landing', ...payload });
      // refresh and confirm
      const fresh = await api.get('/api/cms');
      const landing = (Array.isArray(fresh.data) ? fresh.data : fresh.data?.data || []).find(i => i.type === 'landing');
      setLandingForm({ backgrounds: landing?.contentJSON?.backgrounds || {} });
      setSnackbar({ open: true, message: 'Landing backgrounds saved', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save landing backgrounds', severity: 'error' });
    } finally {
      setLandingLoading(false);
    }
  };

  // Save About content to CMS
  const saveAboutToBackend = async () => {
    setAboutLoading(true);
    try {
      // Check if About item exists
      const current = await api.get('/api/cms');
      const aboutItem = (current.data || current).find(i => i.type === 'about');
      const payload = { contentJSON: { ...aboutForm } };
      if (aboutItem && aboutItem._id) {
        await api.put(`/api/cms/${aboutItem._id}`, payload);
      } else {
        try {
          await api.post('/api/cms', { type: 'about', ...payload });
        } catch (err) {
          if (err.response && err.response.status === 409) {
            const fresh = await api.get('/api/cms');
            const freshAbout = (fresh.data || fresh).find(i => i.type === 'about');
            if (freshAbout && freshAbout._id) {
              await api.put(`/api/cms/${freshAbout._id}`, payload);
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        }
      }
      // Optionally show a success message (snackbar)
    } catch (err) {
      // Optionally show an error message
    } finally {
      setAboutLoading(false);
    }
  };



  if (loading) {
    return (<Box sx={{ p: 3 }}><CircularProgress /></Box>);
  }

  return (
    <StaffLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          CMS Management
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        <NotificationSnackbar {...snackbar} onClose={() => setSnackbar(s => ({ ...s, open: false }))} />
        <Paper sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            {cmsTabs.map((t, i) => <Tab key={t.key} label={t.label} />)}
          </Tabs>
          <Divider />
          <Box sx={{ p: 3 }}>
            {error ? (
              <Typography color="error" sx={{ mt: 2 }}>Unable to load data due to rate limiting. Please try again later.</Typography>
            ) : (
              <>
                
                {tab === 1 && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>Schedule</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Time</TableCell>
                          <TableCell>Activity</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(cms.schedule?.contentJSON?.items || []).length === 0 && <TableRow><TableCell colSpan={2}>No schedule items</TableCell></TableRow>}
                        {(cms.schedule?.contentJSON?.items || []).map((item, i) => (
                          <TableRow key={i}>
                            <TableCell>{item.time}</TableCell>
                            <TableCell>{item.activity}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
                {tab === 2 && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>Highlights</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {(cms.highlight?.contentJSON?.items || []).length === 0 && <Typography color="text.secondary">No highlights</Typography>}
                      {(cms.highlight?.contentJSON?.items || []).map((hl, i) => (
                        <Card key={i} sx={{ width: 270, minHeight: 180, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          {hl.image && <img src={hl.image} alt="highlight" style={{ width: '100%', height: 120, objectFit: 'cover', borderTopLeftRadius: 8, borderTopRightRadius: 8 }} />}
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight={700}>{hl.heading}</Typography>
                            <Typography variant="body2" color="text.secondary">{hl.story}</Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </>
                )}
                {tab === 3 && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>Hotels</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Address</TableCell>
                          <TableCell>Rooms</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {hotels.length === 0 && <TableRow><TableCell colSpan={3}>No hotels</TableCell></TableRow>}
                        {hotels.map((hotel, i) => (
                          <TableRow key={hotel._id || i}>
                            <TableCell>{hotel.name}</TableCell>
                            <TableCell>{hotel.address}</TableCell>
                            <TableCell>{(hotel.rooms || []).map((r, j) => <Chip key={j} label={`${r.type} - ${r.price}`} size="small" sx={{ mr: 0.5 }} />)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
                {tab === 4 && (
                  <SponsorsTab />
                )}
                {tab === 5 && (
                  <ContactTab />
                )}
                {tab === 7 && (
                  <Box sx={{ maxWidth: 650, mx: 'auto', mt: 2, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 2, p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Landing Page Backgrounds</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>Set background images for public pages (excludes Contact and Venues).</Typography>
                    {['home','about','packages','accommodations','schedule','highlights','privacy','terms'].map(key => (
                      <TextField key={key} label={`${key.charAt(0).toUpperCase() + key.slice(1)} Background URL`} fullWidth value={landingForm.backgrounds?.[key] || ''} onChange={e => setLandingForm(f => ({ ...f, backgrounds: { ...(f.backgrounds||{}), [key]: e.target.value } }))} sx={{ mb: 2 }} />
                    ))}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button variant="contained" color="primary" onClick={saveLandingForStaff} disabled={landingLoading}>{landingLoading ? 'Saving...' : 'Save'}</Button>
                      <Button variant="outlined" onClick={() => setLandingForm({ backgrounds: {} })}>Clear</Button>
                    </Box>
                  </Box>
                )}
                {tab === 6 && (
                  <Box sx={{ maxWidth: 650, mx: 'auto', mt: 2, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 2, p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Edit About Us Page</Typography>
                    <TextField
                      label="Heading"
                      fullWidth
                      value={aboutForm.heading}
                      onChange={e => setAboutForm(f => ({ ...f, heading: e.target.value }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="About Text"
                      multiline
                      minRows={3}
                      fullWidth
                      value={aboutForm.text}
                      onChange={e => setAboutForm(f => ({ ...f, text: e.target.value }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Image URL"
                      fullWidth
                      value={aboutForm.image}
                      onChange={e => setAboutForm(f => ({ ...f, image: e.target.value }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Link (optional)"
                      fullWidth
                      value={aboutForm.link}
                      onChange={e => setAboutForm(f => ({ ...f, link: e.target.value }))}
                      sx={{ mb: 2 }}
                    />
                    {/* Live Preview */}
                    <Box sx={{ mt: 3, mb: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default', color: 'text.primary' }}>
                      <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 1 }}>{aboutForm.heading || 'About Us Heading'}</Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{aboutForm.text || 'About us description will appear here.'}</Typography>
                      {aboutForm.image && (
                        <img src={aboutForm.image} alt="About preview" style={{ width: '100%', maxWidth: 320, borderRadius: 8, marginBottom: 16, boxShadow: 2 }} />
                      )}
                      {aboutForm.link && (
                        <Button href={aboutForm.link} target="_blank" rel="noopener" variant="outlined" color="secondary" sx={{ mt: 1 }}>Learn More</Button>
                      )}
                    </Box>
                    <Button variant="contained" color="primary" onClick={saveAboutToBackend} disabled={aboutLoading}>
                      {aboutLoading ? 'Saving...' : 'Save'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </StaffLayout>
  );
}

export default StaffCMS;
